import { useLazyQuery, useMutation } from "@apollo/client";
import { useMsal } from "@azure/msal-react";
import { CloudFramePrimaryButton, CloudFrameDefaultButton } from "@cloudframe/button";
import { CloudFrameTextBox } from "@cloudframe/textbox";
import { DialogType, IIconProps, Icon, Modal, getTheme } from "@fluentui/react";
import { CloudFrameDialog } from "@cloudframe/dialog";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ACTIVATE_MACHINE,
  CONFIRM_DEACTIVATION,
  REQUEST_DEACTIVATION,
} from "../../graphql-mutations/continuum-scanner-license.mutation";
import {
  QUERY_GET_ACTIVATION_KEY_HISTORY,
  QUERY_GET_DEACTIVATION_KEY_HISTORY,
  QUERY_GET_MACHINE_HISTORY,
} from "../../graphql-query/continuum-scanner-license.query";
import { decompress } from "../../utils/compress.utils";
import { isKeyExpired, formatCountdown } from "./deactivation-key-cooldown.utils";
import {
  assertIsActivationFlowFingerprint,
  assertIsDeactivationFlowFingerprint,
} from "./continuum-fingerprint-flow.utils";
import { useStyles } from "./continuum-machine-activation.styles";

const theme = getTheme();

type MachineStatus = "Pending" | "Active" | "DeactivationRequested" | "Deactivated";
type TabKey = "activate" | "deactivate";

interface ContinuumMachineActivationProps {
  licenseId: string;
}

const decompressIcon: IIconProps = { iconName: "Sync" };
const generateIcon: IIconProps = { iconName: "AddTo" };
const copyIcon: IIconProps = { iconName: "Copy" };
const copiedIcon: IIconProps = { iconName: "CheckMark" };
const closeIcon: IIconProps = { iconName: "Cancel" };
const historyIcon: IIconProps = { iconName: "History" };
const backIcon: IIconProps = { iconName: "Back" };

const modalStyles = {
  main: {
    padding: "24px",
    borderRadius: "8px",
    backgroundColor: theme.palette.white,
    boxShadow: theme.effects.elevation8,
    maxWidth: "560px",
  },
};

const statusLabel = (status: MachineStatus): string => {
  if (status === "Active") return "Active";
  if (status === "DeactivationRequested") return "Deactivation Requested";
  if (status === "Deactivated") return "Deactivated";
  return "No Machine";
};

// Maps each machine status to the tabs allowed next, per the status transition rules:
// Pending -> Active only; Active/DeactivationRequested -> both; Deactivated -> Active only.
const getAllowedTabs = (status: MachineStatus): Record<TabKey, boolean> => {
  switch (status) {
    case "Active":
    case "DeactivationRequested":
      return { activate: true, deactivate: true };
    case "Pending":
    case "Deactivated":
    default:
      return { activate: true, deactivate: false };
  }
};

const ContinuumMachineActivation = ({ licenseId }: ContinuumMachineActivationProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const msl = useMsal();

  const [activeTab, setActiveTab] = useState<"activate" | "deactivate">("activate");

  const [rawFingerprint, setRawFingerprint] = useState("");
  const [decompressedFields, setDecompressedFields] = useState<Record<string, any> | null>(null);
  const [decompressError, setDecompressError] = useState<string | null>(null);

  const [confirmationPayload, setConfirmationPayload] = useState("");
  const [showDeactivateSuccess, setShowDeactivateSuccess] = useState(false);

  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [isKeyModalOpen, setIsKeyModalOpen] = useState(false);

  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [deactivationKeyCopied, setDeactivationKeyCopied] = useState(false);
  const [activationKeyCopied, setActivationKeyCopied] = useState(false);

  const [loadMachineHistory, { data: machineData }] = useLazyQuery(
    QUERY_GET_MACHINE_HISTORY,
    { fetchPolicy: "network-only" }
  );
  const [loadActivationHistory] = useLazyQuery(QUERY_GET_ACTIVATION_KEY_HISTORY, {
    fetchPolicy: "network-only",
  });
  const [loadDeactivationHistory, { data: deactivationData }] = useLazyQuery(
    QUERY_GET_DEACTIVATION_KEY_HISTORY,
    { fetchPolicy: "network-only" }
  );

  const [activateMachine] = useMutation(ACTIVATE_MACHINE);
  const [requestDeactivation] = useMutation(REQUEST_DEACTIVATION);
  const [confirmDeactivation] = useMutation(CONFIRM_DEACTIVATION);

  const machines = machineData?.getMachineHistory || [];
  const currentMachine =
    machines.find((m: any) => m.status === "DeactivationRequested") ??
    machines.find((m: any) => m.status === "Active");
  const currentStatus: MachineStatus = currentMachine?.status ?? "Pending";
  const allowedTabs = getAllowedTabs(currentStatus);

  const outstandingDeactivationKey = (deactivationData?.getDeactivationKeyHistory || []).find(
    (k: any) => !k.consumedAt && k.machineId === currentMachine?.id
  );

  const [nowTick, setNowTick] = useState(() => Date.now());

  const keyIsExpired =
    !outstandingDeactivationKey || isKeyExpired(outstandingDeactivationKey.expiresAt, nowTick);

  const showOutstandingKey = !!outstandingDeactivationKey && !keyIsExpired;

  useEffect(() => {
    if (!outstandingDeactivationKey || keyIsExpired) return;
    const intervalId = setInterval(() => setNowTick(Date.now()), 60000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outstandingDeactivationKey?.id, keyIsExpired]);

  const generateKeyDisabled = currentStatus !== "Active" || showOutstandingKey;

  useEffect(() => {
    if (!deactivationKeyCopied) return;
    const timer = setTimeout(() => setDeactivationKeyCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [deactivationKeyCopied]);

  useEffect(() => {
    if (!activationKeyCopied) return;
    const timer = setTimeout(() => setActivationKeyCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [activationKeyCopied]);

  const copyToClipboard = async (text: string, onCopied: () => void) => {
    try {
      await navigator.clipboard.writeText(text);
      onCopied();
    } catch (err) {
      console.error("Unable to copy to clipboard.", err);
    }
  };

  const refetchAll = () => {
    loadMachineHistory({ variables: { licenseId } });
    loadActivationHistory({ variables: { licenseId } });
    loadDeactivationHistory({ variables: { licenseId } });
  };

  // Keeps the generic heading the user recognizes, but surfaces the actual
  // reason underneath instead of only logging it to the console.
  const buildActivationErrorMessage = (reason: string): string =>
    `Unable to activate this machine\n(${reason})`;

  useEffect(() => {
    refetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseId]);

  const switchTab = (tab: TabKey) => {
    if (!allowedTabs[tab]) return;
    setActiveTab(tab);
    setShowDeactivateSuccess(false);
    setDecompressError(null);
    setError(null);
  };

  useEffect(() => {
    if (!allowedTabs[activeTab]) {
      setActiveTab("activate");
      setShowDeactivateSuccess(false);
      setDecompressError(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStatus]);

  const handleDecompressFingerprint = () => {
    setDecompressError(null);
    setDecompressedFields(null);
    if (!rawFingerprint) return;
    try {
      const decompressedData = decompress(rawFingerprint);
      if (!decompressedData) {
        console.error("Unable to decompress this fingerprint.");
        setDecompressError("Hardware fingerprint is not valid.");
        return;
      }
      const parsed = JSON.parse(decompressedData);
      if (String(parsed.licenseId) !== String(licenseId)) {
        setDecompressError(
          `This fingerprint belongs to license ${parsed.licenseId}, not license ${licenseId}.`
        );
        return;
      }
      try {
        assertIsActivationFlowFingerprint(parsed);
      } catch (flowErr: any) {
        console.error(flowErr.message);
        setDecompressError("Hardware fingerprint is not valid.");
        return;
      }
      setDecompressedFields(parsed);
    } catch (err) {
      console.error("Invalid hardware fingerprint.", err);
      setDecompressError("Hardware fingerprint is not valid.");
    }
  };

  const handleActivateNewMachine = async () => {
    if (!rawFingerprint) return;
    setError(null);
    try {
      const decompressedData = decompress(rawFingerprint);
      if (!decompressedData) {
        console.error("Unable to decompress this fingerprint.");
        setError("Hardware fingerprint is not valid.");
        return;
      }
      const parsed = JSON.parse(decompressedData);
      if (String(parsed.licenseId) !== String(licenseId)) {
        setError(
          `This fingerprint belongs to license ${parsed.licenseId}, not license ${licenseId}.`
        );
        return;
      }
      try {
        assertIsActivationFlowFingerprint(parsed);
      } catch (flowErr: any) {
        console.error(flowErr.message);
        setError("Hardware fingerprint is not valid.");
        return;
      }
      const machineFingerprint = parsed.machineFingerprint ?? parsed.hardwareFingerprints?.[0];
      if (!machineFingerprint) {
        console.error("Decompressed fingerprint is missing machineFingerprint.");
        setError("Hardware fingerprint is not valid.");
        return;
      }

      const result = await activateMachine({
        variables: {
          licenseId,
          machineFingerprint,
          activationEmail: msl.accounts?.[0]?.username || "",
          previousLicenseId: parsed.previousLicenseId,
          reportedLinesUsed: parsed.usageFingerprint?.linesUsed,
          reportedEffectiveLimit: parsed.usageFingerprint?.effectiveLimit,
        },
      });
      setGeneratedKey(result.data.activateMachine.activationKey);
      setIsKeyModalOpen(true);
      setActivationKeyCopied(false);
      setRawFingerprint("");
      setDecompressedFields(null);
      refetchAll();
    } catch (err: any) {
      console.error(err.message);
      setError(buildActivationErrorMessage(err.message));
    }
  };

  const handleRequestDeactivation = async () => {
    setError(null);
    try {
      await requestDeactivation({ variables: { licenseId } });
      setShowDeactivateConfirm(false);
      refetchAll();
    } catch (err: any) {
      console.error(err.message);
      setError("Unable to request deactivation.");
      setShowDeactivateConfirm(false);
    }
  };

  const handleConfirmDeactivation = async () => {
    if (!confirmationPayload) return;
    setError(null);
    try {
      const decompressedData = decompress(confirmationPayload);
      if (!decompressedData) {
        console.error("Unable to decompress this fingerprint.");
        setError("Confirmation fingerprint is not valid.");
        return;
      }
      const parsed = JSON.parse(decompressedData);
      try {
        assertIsDeactivationFlowFingerprint(parsed);
      } catch (flowErr: any) {
        console.error(flowErr.message);
        setError("Confirmation fingerprint is not valid.");
        return;
      }
      if (
        outstandingDeactivationKey &&
        parsed.deactivationRequestId !== outstandingDeactivationKey.deactivationRequestId
      ) {
        setError(
          "This fingerprint doesn't match the current outstanding deactivation request for this machine. Make sure you're pasting the fingerprint returned for the deactivation key shown above, not one from a different machine or an earlier request."
        );
        return;
      }
    } catch (err) {
      console.error("Invalid confirmation fingerprint.", err);
      setError("Confirmation fingerprint is not valid.");
      return;
    }
    try {
      await confirmDeactivation({
        variables: { licenseId, deactivationConfirmationPayload: confirmationPayload },
      });
      setConfirmationPayload("");
      setShowDeactivateSuccess(true);
      refetchAll();
    } catch (err: any) {
      console.error(err.message);
      setError("Unable to confirm deactivation.");
    }
  };

  const statusBadgeClass = () => {
    if (currentStatus === "Active") return classes.statusActive;
    if (currentStatus === "DeactivationRequested") return classes.statusRequested;
    if (currentStatus === "Deactivated") return classes.statusDeactivated;
    return classes.statusNone;
  };

  const detailRows: { label: string; value: any }[] = decompressedFields
    ? [
        { label: "License ID", value: decompressedFields.licenseId },
        { label: "Previous license ID", value: decompressedFields.previousLicenseId },
        { label: "Machine fingerprint", value: decompressedFields.machineFingerprint },
        { label: "Hardware fingerprints", value: decompressedFields.hardwareFingerprints?.join(", ") },
        { label: "Lines used", value: decompressedFields.usageFingerprint?.linesUsed },
        { label: "Modernize lines used", value: decompressedFields.usageFingerprint?.modernizeLinesUsed },
        { label: "Total limit", value: decompressedFields.usageFingerprint?.totalLimit },
        { label: "Effective limit", value: decompressedFields.usageFingerprint?.effectiveLimit },
        { label: "Carry-over inbound", value: decompressedFields.usageFingerprint?.carryOverInbound },
        { label: "Carry-over outbound", value: decompressedFields.usageFingerprint?.carryOverOutbound },
      ]
    : [];

  return (
    <div className={classes.root}>
      <div className={classes.card}>
        <div className={classes.headerRow}>
          <div className={classes.headerTitleBlock}>
            <CloudFrameDefaultButton
              text="Back to all licenses"
              iconProps={backIcon}
              onClick={() => navigate("/all-continuum-scanner-licenses")}
              style={{ marginBottom: "0.75rem", alignSelf: "flex-start" }}
            />
            <h2 className={classes.cardTitle}>License key management</h2>
            <span className={classes.headerSubtitle}>License ID {licenseId}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", columnGap: "0.75rem" }}>
            <span className={`${classes.statusBadge} ${statusBadgeClass()}`}>
              {statusLabel(currentStatus)}
            </span>
            <CloudFrameDefaultButton
              text="View history"
              iconProps={historyIcon}
              onClick={() => navigate(`/continuum-machine-activation/${licenseId}/history`)}
            />
          </div>
        </div>

        <div className={classes.pillTabs}>
          <button
            type="button"
            className={`${classes.pillTab} ${activeTab === "activate" ? classes.pillTabActive : classes.pillTabInactive}`}
            onClick={() => switchTab("activate")}
            disabled={!allowedTabs.activate}
          >
            Activate
          </button>
          <button
            type="button"
            className={`${classes.pillTab} ${
              !allowedTabs.deactivate
                ? classes.pillTabDisabled
                : activeTab === "deactivate"
                ? classes.pillTabActive
                : classes.pillTabInactive
            }`}
            onClick={() => switchTab("deactivate")}
            disabled={!allowedTabs.deactivate}
          >
            Deactivate
          </button>
        </div>

        {error && <div className={classes.errorBanner}>{error}</div>}

        {activeTab === "activate" && (
          <div>
            <span className={classes.fieldLabel}>Hardware fingerprint</span>
            <CloudFrameTextBox
              multiline
              rows={4}
              resizable={true}
              className={classes.mb8}
              value={rawFingerprint}
              onChange={(_, v) => {
                setRawFingerprint(v || "");
                setDecompressedFields(null);
                setDecompressError(null);
              }}
            />
            <div className={classes.btnRow}>
              <CloudFrameDefaultButton
                text="Decompress fingerprint"
                iconProps={decompressIcon}
                onClick={handleDecompressFingerprint}
                disabled={!rawFingerprint}
              />
              <CloudFramePrimaryButton
                text="Generate activation key"
                iconProps={generateIcon}
                onClick={handleActivateNewMachine}
                disabled={!rawFingerprint}
              />
            </div>

            {decompressError && <div className={classes.errorBanner}>{decompressError}</div>}

            {detailRows.length > 0 && (
              <>
                <hr className={classes.divider} />
                <div className={classes.detailsSection}>
                  <h3 className={classes.cardTitle}>License details</h3>
                  {detailRows.map((row) => (
                    <div key={row.label} className={classes.detailRow}>
                      <span className={classes.detailLabel}>{row.label}</span>
                      <span className={classes.detailValue}>
                        {row.value === undefined || row.value === null ? "N/A" : String(row.value)}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === "deactivate" && (
          <div>
            <div className={classes.warningBanner}>
              Deactivation is a two-step process: generate a deactivation key for the
              customer, then confirm once they send back their fingerprint.
            </div>

            <div className={classes.stepRow}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div className={`${classes.stepBadge} ${classes.stepBadgeGreen}`}>
                  <Icon iconName="Add" style={{ fontSize: "0.8rem" }} />
                </div>
                <div className={classes.stepConnector} />
              </div>
              <div className={classes.stepContent}>
                <div className={classes.stepTitle}>Generate deactivation key</div>
                <div className={classes.stepSubtitle}>
                  Generate a key for this license and send it to the customer to deactivate on their end.
                </div>
                <div className={classes.readonlyBox}>
                  {currentMachine?.machineFingerprint ?? "No active machine"}
                </div>
                <CloudFramePrimaryButton
                  text="Generate deactivation key"
                  className={
                    generateKeyDisabled
                      ? classes.generateKeyButtonDisabled
                      : classes.generateKeyButtonEnabled
                  }
                  onClick={() => setShowDeactivateConfirm(true)}
                  disabled={generateKeyDisabled}
                />

                {showOutstandingKey && (
                  <div className={`${classes.codeBox} ${classes.mt16}`}>
                    <div className={classes.codeBoxHeader}>
                      <span className={classes.keyLabel}>
                        Deactivation key &middot; Expires in{" "}
                        {formatCountdown(outstandingDeactivationKey.expiresAt, nowTick)}
                      </span>
                      <CloudFrameDefaultButton
                        text={deactivationKeyCopied ? "Copied" : "Copy"}
                        iconProps={deactivationKeyCopied ? copiedIcon : copyIcon}
                        onClick={() =>
                          copyToClipboard(outstandingDeactivationKey.deactivationKey, () =>
                            setDeactivationKeyCopied(true)
                          )
                        }
                      />
                    </div>
                    {outstandingDeactivationKey.deactivationKey}
                  </div>
                )}
              </div>
            </div>

            <div className={classes.stepRow}>
              <div>
                <div className={`${classes.stepBadge} ${classes.stepBadgeBlue}`}>2</div>
              </div>
              <div className={classes.stepContent}>
                <div className={classes.stepTitle}>Confirm deactivation</div>
                <div className={classes.stepSubtitle}>
                  Once the customer finishes deactivating on their end, they send back their
                  fingerprint. Paste it here to confirm.
                </div>
                <CloudFrameTextBox
                  multiline
                  rows={4}
                  resizable={true}
                  className={classes.mb8}
                  placeholder="Paste returned fingerprint"
                  value={confirmationPayload}
                  onChange={(_, v) => setConfirmationPayload(v || "")}
                />
                <CloudFramePrimaryButton
                  text="Confirm & mark deactivated"
                  style={{ backgroundColor: "#d13438", borderColor: "#d13438" }}
                  onClick={handleConfirmDeactivation}
                  disabled={!showOutstandingKey || !confirmationPayload}
                />

                {showDeactivateSuccess && (
                  <div className={classes.successBanner}>
                    License {licenseId} marked as deactivated.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <CloudFrameDialog
        hidden={!showDeactivateConfirm}
        onDismiss={() => setShowDeactivateConfirm(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: "Confirm Deactivation Request",
          subText:
            "This will issue a deactivation key for the current machine. The license stays bound to this machine until the product confirms teardown.",
        }}
        primaryButton={{ text: "Request Key", onClick: handleRequestDeactivation }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => setShowDeactivateConfirm(false),
        }}
      />

      <Modal
        isOpen={isKeyModalOpen}
        onDismiss={() => setIsKeyModalOpen(false)}
        isBlocking={false}
        styles={modalStyles}
      >
        <div className={classes.codeBoxHeader}>
          <Icon iconName="CompletedSolid" style={{ color: "#107c10", fontSize: "1.3rem" }} />
          <h3 style={{ margin: 0 }}>Activation key generated</h3>
        </div>
        <p className={classes.headerSubtitle}>
          Copy this key and share it with the customer to complete activation.
        </p>
        <div className={classes.codeBox}>{generatedKey}</div>
        <div className={classes.btnRow}>
          <CloudFramePrimaryButton
            text={activationKeyCopied ? "Copied" : "Copy activation key"}
            iconProps={activationKeyCopied ? copiedIcon : copyIcon}
            onClick={() =>
              generatedKey &&
              copyToClipboard(generatedKey, () => setActivationKeyCopied(true))
            }
          />
          <CloudFrameDefaultButton
            text="Close"
            iconProps={closeIcon}
            onClick={() => setIsKeyModalOpen(false)}
          />
        </div>
      </Modal>
    </div>
  );
};

export default ContinuumMachineActivation;
